import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Event } from '../../models/event/event';
import { cn } from '../../utils/class-names-util';

interface OngoingEventsSectionProps {
  events: Event[];
  className?: string;
}

const OngoingEventsSection: React.FC<OngoingEventsSectionProps> = ({
  events,
  className
}) => {
  const { t, i18n } = useTranslation();

  if (events.length === 0) {
    return null;
  }

  const formatTime = (timeString: string) => {
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={cn("bg-white border border-gray-200 rounded-xl shadow-sm", className)}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('pages.events.ongoingEvents')}
            </h2>
            <span className="ml-3 px-3 py-1 bg-black text-white text-sm rounded-full font-medium">
              {events.length}
            </span>
          </div>
          <p className="text-gray-600">
            {t('pages.events.currentlyHappening')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => {
            return (
              <div
                key={event.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md"
              >
                {/* Event Image */}
                {(event.media.cover?.url || '') && (
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={event.media.cover?.url || ''}
                      alt={event.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/400x300?text=Event+Image';
                      }}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center px-3 py-1 bg-black text-white text-xs rounded-full font-medium">
                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></div>
                        {t('pages.events.status.ongoing')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-4">
                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {event.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {event.shortDescription}
                  </p>

                  {/* Event Info */}
                  <div className="space-y-2 mb-4">
                    {/* Time */}
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {formatTime(event.startTime)} - {formatTime(event.endTime)}
                      </span>
                    </div>

                    {/* Location */}
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="line-clamp-1">
                        {event.address.name}
                      </span>
                    </div>

                    {/* Category */}
                    <div className="flex items-center">
                      <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-medium">
                        {t(`pages.events.categories.${event.category}`)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4">
                    <Link
                      to={`/events/${event.key}/${event.id}`}
                      className="w-full inline-flex items-center justify-center px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {t('pages.events.card.viewDetails')}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OngoingEventsSection;